<template>
  <div>
    <b-jumbotron>
      <b-row>
        <font-awesome-layers full-width class="fa-3x logo">
          <font-awesome-icon icon="square" style="color: #85458a" />
          <font-awesome-icon
            icon="camera"
            transform="shrink-7"
            style="color: white"
          />
        </font-awesome-layers>
        <h5>Patient Scans</h5>
        <b-col offset="4" style="text-align: right">
          <b-button
            id="idUploadScan"
            size="sm"
            variant="outline-primary"
            v-b-modal="'idUploadScanModal'"
            >Upload Scan</b-button
          >
          <UploadScanModal @okClicked="uploadPatientScanClicked" />
          <ShareScanModal
            :patientScanId="modalScanId"
            @refreshPatientScans="getPatientScans"
          />
        </b-col>
      </b-row>
    </b-jumbotron>
    <b-jumbotron>
      <b-row>
        <b-col>
          <label for="selectStatus">Status</label>
          <b-form-select
            size="sm"
            id="selectStatus"
            v-model="selectedStatus"
            :options="statusOptions"
          ></b-form-select>
        </b-col>
        <b-col>
          <label for="selectedUploadedWithin">Uploaded within</label>
          <b-form-select
            size="sm"
            id="selectedUploadedWithin"
            v-model="selectedUploadedWithin"
            :options="uploadedWithinOptions"
          ></b-form-select>
        </b-col>
        <b-col>
          <label for="selectedAssignedBy">Assigned By</label>
          <b-form-select
            size="sm"
            id="selectedAssignedBy"
            v-model="selectedAssignedBy"
            :options="assignedByOptions"
          ></b-form-select>
        </b-col>
        <b-col>
          <label for="filterInput">Search</label>
          <b-form-input
            size="sm"
            v-model="filter"
            type="search"
            id="filterInput"
            placeholder="Type to Search"
          ></b-form-input>
        </b-col>
      </b-row>
    </b-jumbotron>
    <b-row style="margin: 0 1rem">
      <b-table
        style="text-align: left"
        v-if="!showSpinner"
        id="idPatientScans"
        head-variant="light"
        outlined
        show-empty
        small
        per-page="20"
        :current-page="currentPage"
        :items="scans"
        :fields="fields"
        :filter="filter"
        @filtered="onFiltered"
      >
        <template v-slot:head(actions)>
          <div id="actions-header-div">
            <span v-bind:style="specialHeader">Actions</span>
            <b-button
              size="sm"
              variant="outline-dark"
              class="refresh-btn"
              @click="getPatientScans()"
              ><b-icon scale=".9" icon="arrow-repeat"></b-icon>
              {{ refreshBtnText }}</b-button
            >
            <b-dropdown
              id="more-columns-dropdown"
              text="More Columns"
              size="sm"
              variant="outline-primary"
              dropleft
              ref="moreColumnsDropdown"
            >
              <b-dropdown-form>
                <b-form-checkbox
                  class="column-dropdown"
                  value="SeriesDescription"
                  v-model="checkedColumns"
                  ><b>Series Description</b></b-form-checkbox
                >
                <b-form-checkbox
                  class="column-dropdown"
                  value="StudyDescription"
                  v-model="checkedColumns"
                  ><b>Study Description</b></b-form-checkbox
                >
                <b-button
                  size="sm"
                  variant="outline-primary"
                  id="apply-columns-btn"
                  @click="applyMoreColumns()"
                  >Apply</b-button
                >
              </b-dropdown-form>
            </b-dropdown>
          </div>
        </template>
        <template v-slot:cell(actions)="row">
          <div
            v-if="row.item.IsZipping"
            style="margin: 0.4rem 0rem 0.2rem 0rem"
            class="progressText"
          >
            <b-spinner
              style="width: 1rem; height: 1rem"
              variant="primary"
            ></b-spinner>
            Zipping Directory...
          </div>
          <div
            v-else-if="
              row.item.IsUploading == true &&
              row.item.UploadProgress >= row.item.UploadMax
            "
            style="margin: 0.4rem 0rem 0.2rem 0rem"
            class="progressText"
          >
            <b-spinner
              style="width: 1rem; height: 1rem"
              variant="primary"
            ></b-spinner>
            Finalizing Upload...
          </div>
          <b-progress
            v-else-if="
              row.item.IsUploading == true &&
              row.item.UploadProgress < row.item.UploadMax
            "
            id="idUploadProgress"
            variant="primary"
            :max="row.item.UploadMax"
            show-progress
          >
            <b-progress-bar
              :value="row.item.UploadProgress"
              :label="`${(row.item.UploadProgress / 1000000).toFixed(0)}MB / ${(
                row.item.UploadMax / 1000000
              ).toFixed(0)}MB`"
            ></b-progress-bar>
          </b-progress>
          <b-button-group
            v-else-if="row.item.IsUploading == false || !row.item.IsUploading"
          >
            <!-- Dicom Viewer Button - all buttons with all styles initialized, but only one visible at a time -->
            <b-button
              class="viewer-btn"
              v-if="row.item.ProvisioningStatus === 'Unprovisioned'"
              size="sm"
              @click="handleDicomButtonClick(row.item)"
              variant="outline-primary"
            >
              <b-icon scale=".9" icon="eye"></b-icon> Click to Load
            </b-button>

            <!-- Completed Button -->
            <b-button
              class="viewer-btn"
              v-else-if="row.item.ProvisioningStatus === 'Completed'"
              size="sm"
              @click="handleDicomButtonClick(row.item)"
              variant="outline-success"
            >
              <b-icon scale=".9" icon="check-circle-fill"></b-icon> Click to
              View
            </b-button>

            <!-- Failed Button -->
            <b-button
              class="viewer-btn"
              v-else-if="row.item.ProvisioningStatus === 'Failed'"
              size="sm"
              @click="handleDicomButtonClick(row.item)"
              variant="outline-danger"
            >
              <b-icon scale=".9" icon="x-circle-fill"></b-icon> Click to Retry
            </b-button>

            <!-- Purging Button -->
            <b-button
              class="viewer-btn"
              v-else-if="row.item.ProvisioningStatus === 'Purging'"
              disabled
              size="sm"
              @click="handleDicomButtonClick(row.item)"
              variant="outline-primary"
            >
              <b-icon scale=".9" icon="trash-fill" animation="throb"></b-icon>
              Purging...</b-button
            >
            <!-- For provisioning statuses InProgress or status not set yet, show the loading button -->
            <b-button
              class="viewer-btn"
              v-else
              size="sm"
              disabled
              variant="outline-primary"
            >
              <b-icon
                scale=".9"
                icon="arrow-clockwise"
                animation="spin"
              ></b-icon>
              Loading...
            </b-button>

            <!-- Download Patient Scan button-->
            <b-button
              size="sm"
              variant="outline-primary"
              @click="downloadPatientScanClicked(row.item)"
            >
              <b-icon scale=".9" icon="download"></b-icon> Download
            </b-button>

            <!-- Share Patient Scan button-->
            <b-button
              size="sm"
              variant="outline-primary"
              @click="sharePatientScanClicked(row.item.Id)"
              v-b-modal="'idShareScanModal'"
              v-if="isFieldUser || isInhouseUser"
            >
              <b-icon scale=".9" icon="share"></b-icon> Share
            </b-button>
          </b-button-group>
        </template>
        <template v-slot:cell(shared)="row">
          <div>
            <b-icon
              scale="1.15"
              icon="person-fill"
              v-if="row.item.SharedUsers && row.item.SharedUsers.length > 0"
              class="shared-icon"
            ></b-icon>
          </div>
        </template>
      </b-table>
    </b-row>
    <b-pagination
      style="margin-right: 1rem"
      v-if="!showSpinner"
      v-model="currentPage"
      :total-rows="totalRows"
      first-number
      last-number
      per-page="20"
      align="right"
    />
    <div style="text-align: center">
      <b-spinner
        variant="warning"
        class="m-5"
        style="width: 3rem; height: 3rem"
        v-if="showSpinner"
      />
    </div>
  </div>
</template>

<script>
import UploadScanModal from "@/components/UploadScanModal";
import ShareScanModal from "@/components/ShareScanModal";

import { download } from "../mixins/download";
import { zip } from "../mixins/zip";

import moment from "moment";
import _ from "lodash";
import axios from "axios";
import * as rax from "retry-axios";

export default {
  mixins: [download, zip],
  data() {
    return {
      totalTimeVisible: 1,
      tabFocus: true,
      totalRows: 1,
      currentPage: 1,
      scans: [],
      uploadingScans: [],
      selectedStatus: "Available",
      selectedUploadedWithin: "7",
      statusOptions: [
        { value: "Available", text: "Available" },
        { value: "Assigned", text: "Assigned" },
      ],
      assignedByOptions: [{ value: "All", text: "All" }],
      uploadedWithinOptions: [
        { value: "7", text: "Last 7 days" },
        { value: "30", text: "Last 30 days" },
        { value: "-1", text: "All" },
      ],
      selectedAssignedBy: "All",
      filter: null,
      showSpinner: true,
      statusChecksTimer: null,
      fields: [
        { key: "PatientName", sortable: true },
        { key: "DateOfBirth", sortable: true },
        { key: "Uploader", sortable: true },
        { key: "NumberOfSlices", sortable: true },
        { key: "ModificationDate", sortable: true },
        { key: "ReferringPhysician", sortable: true },
        { key: "Shared", sortable: true },
        { key: "Actions", sortable: false },
      ],
      refreshBtnText: "Refresh Table",
      updateBtnSizeInit: false,
      observer: null,
      specialHeader: {
        position: "absolute",
        top: 10,
        "margin-top": "0.8rem",
      },
      checkedColumns: [],
      appliedCheckedColumns: [],
      dicomTagMap: {
        SeriesDescription: "(0008,103E)",
        StudyDescription: "(0008,1030)",
      },
      modalScanId: "",
      isFieldUser: false,
      isInhouseUser: false,
    };
  },
  components: {
    UploadScanModal,
    ShareScanModal,
  },
  computed: {},
  created() {
    // Warn user when closing tab while uploading
    window.addEventListener(
      "beforeunload",
      (e) => {
        if (this.$store.state.isUploading) {
          e.preventDefault();
          e.returnValue = "";
        }
      },
      false
    );
    this.getPatientScans();

    // Initializes the observer which automatically shrinks refresh table button if too small
    this.observer = new ResizeObserver(this.updateRefreshButtonSize);

    // Only MPS or Seg users have access to share patient scans
    this.isFieldUser = this.$store.state.roles?.includes("RH.MS.FieldUser");
    this.isInhouseUser = this.$store.state.roles?.includes("RH.MS.InhouseUser");
  },
  watch: {
    selectedStatus() {
      this.selectedAssignedBy = "All";
      this.getPatientScans();
    },
    selectedAssignedBy() {
      this.getPatientScans();
    },
    selectedUploadedWithin() {
      this.getPatientScans();
    },
  },
  methods: {
    readFile(file) {
      return new Promise((resolve) => {
        let reader = new FileReader();
        reader.onload = function () {
          resolve(reader.result);
        };
        reader.readAsArrayBuffer(file);
      });
    },
    newId() {
      return "_" + Math.random().toString(36).substr(2, 9);
    },
    async uploadPatientScanClicked(data) {
      let file = null;
      // Create a new temp ID to identify the uploading scan
      const scanId = this.newId();

      // 2.1 If Directory, zip it..
      if (data.droppedDirectory && data.droppedDirectory.length > 0) {
        try {
          // Create a new entry in the table with zipping indication
          let zippingScan = {
            Id: scanId,
            PatientName: data.fullName,
            DateOfBirth:
              !data.discoveredDateOfBirth ||
              data.discoveredDateOfBirth == "Invalid date"
                ? ""
                : moment(data.discoveredDateOfBirth, "YYYY-MM-DD")
                    .utcOffset(0, true)
                    .format("l"),
            ReferringPhysician: data.referringPhysician,
            Uploader: this.$msal.data.user.userName,
            ModificationDate: moment().format("l"),
            IsZipping: true,
          };
          this.uploadingScans.unshift(zippingScan);
          this.scans.unshift(zippingScan);
          file = await this.zipDirectory(data);
        } catch (e) {
          this.$root.$emit("showAlert", {
            type: "error",
            msg: `Error while zipping patient scan directory.`,
          });
          // eslint-disable-next-line
          console.error(e);
          return;
        }
        // 2.2 Else, just add file
      } else if (data.droppedFile) {
        file = data.droppedFile;
      }
      // Create the new scan (including the upload progress config)
      // NOTE: This is the uploading scan - if this contains a bilateral scan, it will split into the 2+ individual scans when done uploading
      let newScan = {
        Id: scanId,
        FileName: file.name,
        UploadMax: file.size,
        PatientName: data.fullName,
        DateOfBirth:
          !data.discoveredDateOfBirth ||
          data.discoveredDateOfBirth == "Invalid date"
            ? ""
            : moment(data.discoveredDateOfBirth, "YYYY-MM-DD")
                .utcOffset(0, true)
                .format("l"),
        ReferringPhysician: data.referringPhysician,
        Uploader: this.$msal.data.user.userName,
        IsUploading: true,
        ModificationDate: moment().format("l"),
        UploadProgress: 0,
        SharedUsers: [],
      };
      const config = {
        onUploadProgress: (progressEvent) => {
          const index = this.scans.findIndex((scan) => scan.Id == scanId);
          if (index !== -1) {
            this.scans[index].UploadProgress = progressEvent.loaded;
          }
        },
      };

      // 3. Upload File
      let formData = new FormData();
      let metadataResponses = null;
      formData.append("file", file);
      try {
        // set upload globally (this disables idle timeout)
        this.$store.commit("SET_IS_UPLOADING", true);

        // if we didn't add the scan to the table for zipping, add our new scan to the uploading array
        // and to the top of the table
        if (
          this.uploadingScans.filter((scan) => scan.Id === newScan.Id).length ==
          0
        ) {
          this.uploadingScans.unshift(newScan);
          this.scans.unshift(newScan);
          // else, scan already exist (zipping), just update
        } else {
          // add UploadMax and IsUploading, remove zipping state
          this.uploadingScans = this.uploadingScans.map((scan) =>
            scan.Id === newScan.Id
              ? {
                  ...scan,
                  IsUploading: true,
                  IsZipping: false,
                  UploadProgress: 0,
                  FileName: newScan.FileName,
                  UploadMax: newScan.UploadMax,
                }
              : scan
          );
          // Update the existing scan in the table too
          this.scans = this.scans.map((scan) =>
            scan.Id === newScan.Id
              ? {
                  ...scan,
                  IsUploading: true,
                  IsZipping: false,
                  UploadProgress: 0,
                  FileName: newScan.FileName,
                  UploadMax: newScan.UploadMax,
                }
              : scan
          );
        }

        // Upload Scan
        let patientScanResponse = await axios.post(
          "/patientscans",
          formData,
          config
        );

        // Wait we get the metadata for each scan from the backend before continuing
        rax.attach();
        metadataResponses = new Array(patientScanResponse.data.length);
        for (let i = 0; i < metadataResponses.length; i++) {
          metadataResponses[i] = await axios({
            url: `patientscans/${patientScanResponse.data[i].patientScanId}/metadata`,
            raxConfig: {
              retryDelay: 5000,
              statusCodesToRetry: [[400, 429]],
              backoffType: "static",
            },
          });
        }
      } catch (e) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${e.status}, scan upload failed.`,
        });
        this.$store.commit("SET_IS_UPLOADING", false);
      }

      this.uploadingScans = this.uploadingScans.filter(
        (scan) => scan.Id !== scanId
      );
      this.$store.commit("SET_IS_UPLOADING", false);

      this.scans = this.scans.filter((scan) => scan.Id !== scanId);

      // Replace uploading scan with potentially several scans if bilateral
      for (const response of metadataResponses) {
        const newId = response.data.metadata.patientScanId;
        const newNumSlices = response.data.metadata.totalNumberOfImages;

        let uploadedScan = {
          Id: newId,
          PatientName: newScan.PatientName,
          DateOfBirth: newScan.DateOfBirth,
          ReferringPhysician: newScan.ReferringPhysician,
          Uploader: newScan.Uploader,
          FileName: newScan.FileName,
          // Set the initial status to default
          ProvisioningStatus: "InProgress",
          IsUploading: false,
          ModificationDate: newScan.ModificationDate,
          NumberOfSlices: newNumSlices,
        };
        this.scans.unshift(uploadedScan);
      }

      this.$root.$emit("showAlert", {
        type: "info",
        msg: `Uploading Patient Scan for ${data.fullName} complete.`,
      });

      // Warn the user if this is a bilateral scan
      if (metadataResponses.length > 1) {
        // Wait 3s before sending the next alert so user can see the first one
        await new Promise((r) => setTimeout(r, 3000));
        this.$root.$emit("showAlert", {
          type: "warning",
          msg: `We detected a bilateral scan for ${data.fullName}`,
        });
      }
    },
    async getStatusesForScans(updateAllScans = false) {
      // Create an array with all scans that should be updated
      let patientScanIds = this.scans
        .filter(
          (scan) => scan.ProvisioningStatus === "InProgress" || updateAllScans
        )
        .map((scan) => scan.Id);

      // If no scans to update, just return
      if (!patientScanIds.length) return;
      // Get Statuses for all Scans in the array
      const metadataResponse =
        await this.$patientScanViewerProvisioningApi.patientscansViewermetadataGet(
          { patientScanIds: patientScanIds, pageSize: 0 }
        );

      // Update Statuses and show success alert if needed
      metadataResponse.content.map((response) => {
        const scan =
          this.scans[
            this.scans.findIndex((el) => el.Id === response.patientScanId)
          ];
        // If this update resulted in a completed scan and we're not still initializing, show a success alert
        if (
          response.provisioningStatus === "Completed" &&
          scan.ProvisioningStatus === "InProgress" &&
          this.statusChecksTimer !== null
        ) {
          this.$root.$emit("showAlert", {
            type: "info",
            msg:
              "Patient Scan loading for " +
              scan.PatientName +
              " complete. Ready to view patient scan.",
          });
          // If this update resulted in a failed scan and we're not still initializing, show a failure alert
        } else if (
          response.provisioningStatus === "Failed" &&
          scan.ProvisioningStatus === "InProgress" &&
          this.statusChecksTimer !== null
        ) {
          this.$root.$emit("showAlert", {
            type: "error",
            msg:
              "Patient Scan loading for " +
              scan.PatientName +
              " failed. Click to retry",
          });
        }
        // Finally, update the provisioning Status
        this.scans[
          this.scans.findIndex((el) => el.Id === response.patientScanId)
        ]["ProvisioningStatus"] = response.provisioningStatus;
      });

      // We have to wait for all the elements to finish rendering before we observe so
      // check every 5 seconds until ready
      const el = document.querySelector("#actions-header-div");
      if (el != null && this.updateBtnSizeInit == false) {
        // If the table header size changes, execute updateRefreshButtonSize function
        this.observer.observe(el.closest("th"));
        this.updateBtnSizeInit = true;
      }
    },
    applyMoreColumns() {
      // Only do anything if the columns changed
      if (
        !_.isEqual(
          this.appliedCheckedColumns.sort(),
          this.checkedColumns.sort()
        )
      ) {
        // First hide the open dialog
        this.$refs.moreColumnsDropdown.hide(true);

        // Initialize with the fields that are always there
        let newFields = [
          { key: "PatientName", sortable: true },
          { key: "DateOfBirth", sortable: true },
          { key: "Uploader", sortable: true },
          { key: "NumberOfSlices", sortable: true },
          { key: "ModificationDate", sortable: true },
          { key: "ReferringPhysician", sortable: true },
        ];

        // Then add the checked columns (or don't add unchecked columns)
        for (const checkedColumn of this.checkedColumns) {
          newFields.push({ key: checkedColumn, sortable: true });
        }

        // Shared and actions always goes last
        newFields.push({ key: "Shared", sortable: false });
        newFields.push({ key: "Actions", sortable: false });
        this.fields = newFields;

        // Populate the new column(s) with data only if we're adding or changing a column
        // (removing a column is already done so no need to call this method)
        if (
          this.appliedCheckedColumns.length < this.checkedColumns.length ||
          (!_.isEqual(
            this.appliedCheckedColumns.sort(),
            this.checkedColumns.sort()
          ) &&
            this.appliedCheckedColumns.length == this.checkedColumns.length)
        ) {
          this.getAdditionalDicomTagData();
        }

        // Then update the object
        this.appliedCheckedColumns = this.checkedColumns;
      }
    },
    async getAdditionalDicomTagData() {
      // Gets the additional specified dicom data for each scan
      let dicomTagsToQuery = [];
      this.checkedColumns.forEach((element) => {
        dicomTagsToQuery.push(this.dicomTagMap[element]);
      });

      this.showSpinner = true;
      // Get the data for each scan
      if (dicomTagsToQuery.length > 0) {
        for (let scan of this.scans) {
          const dicomData =
            await this.$patientScansMetadataApi.patientscansIdMetadataGet(
              scan.Id,
              { dicomTags: dicomTagsToQuery }
            );
          this.checkedColumns.forEach((element) => {
            // Check that the data contains the tag we're looking for
            if (
              typeof dicomData.dicomTags[this.dicomTagMap[element]] !=
              "undefined"
            ) {
              scan[element] = dicomData.dicomTags[this.dicomTagMap[element]];
            }
          });
        }
      }
      this.showSpinner = false;
    },
    async getPatientScans() {
      this.showSpinner = true;
      // Assigned = true, Available = null
      const selectedStatus =  this.selectedStatus === "Assigned" ? true : null;
      this.opts = {
        additionalDataFilter:{ "jointReplacement.tprhs.assigned": selectedStatus},
        pageSize: 0,
      };
      // Get all Scans
      const data = await this.$patientScansMetadataApi.patientscansMetadataGet(
        this.opts
      );

      let scans = await data.content.map(async (scan) => {
        let uploadedBy = "";

        // If the scan already has a status that's NOT "InProgress", keep the status
        let provisioningStatus;
        if (scan.ProvisioningStatus != "InProgress") {
          provisioningStatus = scan.ProvisioningStatus;
        } else {
          provisioningStatus = "InProgress";
        }

        // For automatic uploads get Scan Facility name
        if (!scan.metadata.isManualUpload) {
          try {
            const facility = await this.$buhApi.branchesScanfacilitiesIdGet(
              scan.metadata.uploadedByScanFacility
            );
            uploadedBy = facility.facilityName;
          } catch (e) {
            uploadedBy = "Stryker";
          }
        } else {
          uploadedBy = scan.metadata.uploadedByUser;
        }

        let sharedUsers = [];
        try {
          sharedUsers = scan.additionalData.jointReplacement.sharedScan.users;
        } catch {
          sharedUsers = [];
        }
        // Patient Scans array populated here by JSON Objects
        // Note: All attributes after FileName
        return {
          PatientName: scan.patient.patientName,
          DateOfBirth:
            _.has(scan.patient, "patientDateOfBirth") &&
            scan.patient.patientDateOfBirth !== null
              ? moment(
                  new Date(
                    scan.patient.patientDateOfBirth.getUTCFullYear(),
                    scan.patient.patientDateOfBirth.getUTCMonth(),
                    scan.patient.patientDateOfBirth.getUTCDate()
                  )
                ).format("l")
              : "",
          Uploader: uploadedBy,
          NumberOfSlices: scan.metadata.totalNumberOfImages,
          ModificationDate: moment(scan.metadata.dateTimeLastModified)
            .utc()
            .format("l"),
          ReferringPhysician: scan.metadata.referringPhysicianName,
          Id: scan.metadata.patientScanId,
          ProvisioningStatus: provisioningStatus,
          FileName: scan.metadata.patientScanDescriptor,
          SeriesDescription: "",
          StudyDescription: "",
          DateTimeUploaded: scan.metadata.dateTimeUploaded,
          SharedUsers: sharedUsers,
        };
      });
      await Promise.all(scans).then((scans) => {
        // Filter by "Assigned By"
        if (this.selectedAssignedBy !== "All") {
          scans = scans.filter(
            (scan) => scan.Uploader === this.selectedAssignedBy
          );
        }
        // Filter by "Uploaded Within"
        if (this.selectedUploadedWithin > 0) {
          const today = new Date();
          let startOfRange = new Date();
          startOfRange.setDate(today.getDate() - this.selectedUploadedWithin);
          scans = scans.filter((scan) => startOfRange <= scan.DateTimeUploaded);
        }

        // Merge uploading scans and regular scans
        let mergedScans = [...this.uploadingScans, ...scans];
        this.scans = mergedScans;
        this.totalRows = mergedScans.length;

        // Add "Assigned By" Options for all Uploaders available
        if (this.selectedAssignedBy === "All") {
          this.assignedByOptions = [{ value: "All", text: "All" }];
          scans
            .map((scan) => scan.Uploader)
            .filter((value, index, self) => self.indexOf(value) === index)
            .map((uploader) => {
              this.assignedByOptions.push({ value: uploader, text: uploader });
            });
        }
      });

      // Have to wait for getPatientScans to finish before calling the next one
      await this.getStatusesForScans(true);

      // And get the dicom tag data if it's checked
      await this.getAdditionalDicomTagData();

      // Now stop showing the spinner
      this.showSpinner = false;

      // After done getting all the scans, start the interval to check the statuses periodically (first stop old timers)
      clearInterval(this.statusChecksTimer);
      this.startBackendStatusChecks();
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    handleDicomButtonClick(item) {
      // Only try to open the dicom viewer if the scan succeeded
      if (item["ProvisioningStatus"] === "Completed") {
        this.openDicomViewer(item);
      } else if (item["ProvisioningStatus"] === "Unprovisioned") {
        this.scans = this.scans.map((scan) =>
          scan.Id === item.Id
            ? { ...scan, ProvisioningStatus: "InProgress" }
            : scan
        );
        this.provisionDicomViewer(item);
      } else if (item["ProvisioningStatus"] === "Failed") {
        item.provisioningStatus = "InProgress";
        this.provisionDicomViewer(item);
      } else {
        // eslint-disable-next-line
        console.error(
          "Unimplemented provisioning status: " +
            item["ProvisioningStatus"] +
            " button click"
        );
      }
    },
    provisionDicomViewer(item) {
      try {
        this.$patientScanViewerProvisioningApi.patientscansIdViewerGet(item.Id);
      } catch (error) {
        let errorMsg = "";
        if (error.status === 409) {
          errorMsg = `Another scan with the same Study ID was detected, provisioning aborted`;
        } else {
          errorMsg = `Error ${error.status} while provisioning Patient Scan ${item.id}`;
        }
        this.showSpinner = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
      }
    },
    async openDicomViewer(item) {
      // Show Loading status while we wait for backend
      item.ProvisioningStatus = "InProgress";
      try {
        // Try to get the URL of the viewer from the backend
        const response_obj =
          await this.$patientScanViewerProvisioningApi.patientscansIdViewerGet(
            item.Id
          );
        const ohif_url = response_obj.ohifUrl;
        item.ProvisioningStatus = "Completed";

        // Open and focus on the viewer in a new tab
        let win = window.open(ohif_url, "_blank");
        win.focus();
      } catch (error) {
        let errorMsg = "";
        if (error.status === 409) {
          errorMsg = `Another scan with the same Study ID was detected, provisioning aborted`;
        } else {
          errorMsg = `Error ${error.status} while provisioning Patient Scan ${item.id}`;
        }
        this.showSpinner = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
      }
    },
    async downloadPatientScanClicked(rowItem) {
      try {
        await this.downloadPatientScan(rowItem.Id, rowItem.FileName);
      } catch (error) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Error while downloading Patient Scan",
        });
        // eslint-disable-next-line
        console.error(error);
      }
    },
    async startBackendStatusChecks() {
      // Once every updateScansWithLoadingStatusInSeconds seconds, we check the (provisioning) statuses of the scans
      this.statusChecksTimer = setInterval(() => {
        if (document.visibilityState === "visible") {
          // We count the total time the tab is visible.
          this.totalTimeVisible += this.$updateScansWithLoadingStatusInSeconds;
          // Every time the tab is visible for $updateAllScansInSeconds, we make a call to update all scans
          this.getStatusesForScans(
            this.totalTimeVisible % this.$updateAllScansInSeconds <=
              this.$updateScansWithLoadingStatusInSeconds
          );
        }
      }, this.$updateScansWithLoadingStatusInSeconds * 1000);
    },
    updateRefreshButtonSize() {
      // Gets the width and height of the table header of the actions column
      const width = document
        .querySelector("#actions-header-div")
        .closest("th").offsetWidth;
      const height = document
        .querySelector("#actions-header-div")
        .closest("th").offsetHeight;
      const ratio = width / height;
      // If there are no scans in the table and we have a width/height ratio that indicates we should shrink,
      // remove the button text and change the style
      if (
        this.scans.length == 0 &&
        (ratio < 4 || (this.refreshBtnText = "Refresh Table" && ratio < 4.8))
      ) {
        // More than one line of headers, switch the style and shorten the button text
        this.refreshBtnText = "";
        this.specialHeader = {};
      } else {
        // One line of header, switch to default style and button text
        this.refreshBtnText = "Refresh Table";
        this.specialHeader = {
          position: "absolute",
          top: 10,
          "margin-top": "0.8rem",
        };
      }
    },
    sharePatientScanClicked(patientScanId) {
      this.modalScanId = patientScanId;
    },
  },
  mounted() {},
  beforeRouteLeave(to, from, next) {
    // Stop the update timer before we leave the page
    clearInterval(this.statusChecksTimer);
    next();
  },
};
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions", "@/assets/_stryker.scss";

#idUploadProgress {
  margin: 0.5rem 1rem 0.5rem 0rem;
}
.progressText {
  color: $primary;
}
.viewer-btn {
  width: 9rem;
}
.refresh-btn {
  float: right;
  margin: 0.1rem 0rem 0.1rem 0.6rem;
  font-size: 0.85rem;
}
#more-columns-dropdown {
  float: right;
  margin: 0.1rem 0rem 0.1rem 3.5rem;
}
.column-dropdown {
  padding-left: 1rem;
  padding-top: 0.5rem;
}
#apply-columns-btn {
  margin: 0.8rem 2.3rem 0rem 1.8rem;
  font-size: 0.8rem;
}
.shared-icon {
  margin-left: 1rem;
  margin-top: 0.5rem;
}
</style>